import { createSelector } from 'reselect';

import appSettings from 'wanderu-config';
import Logger from 'wanderu-logger';
import { SnowplowService } from 'wanderu-service-analytics';
import CityService from 'wanderu-service-cities';
import GoogleService from 'wanderu-service-google';

import { formatGoogleLocation } from './utils';

const logger = Logger.getLogger('PopularCities');
const INITIAL_STATE = [];

const NAMESPACE = 'explorer/popularCities';

const SET_CITY_LOCATION = `${NAMESPACE}/SET_CITY_LOCATION`;
const SET_CITY_PICTURE = `${NAMESPACE}/SET_CITY_PICTURE`;
const SET_ALL_CITIES_INACTIVE = `${NAMESPACE}/SET_ALL_CITIES_INACTIVE`;
const SET_CITY_ACTIVE = `${NAMESPACE}/SET_CITY_ACTIVE`;
const REMOVE_ALL_CITIES = `${NAMESPACE}/REMOVE_ALL_CITIES`;

export function fetchPictureByPlaceId(placeId, wcityid) {
    return async (dispatch) => {
        try {
            const url = await GoogleService.getPictureByPlaceId(placeId, { maxwidth: 552 });
            return dispatch(setCityPicture(wcityid, url));
        } catch (ex) {
            const domainUserInfo = appSettings.env.isClient
                ? SnowplowService.getDomainUserInfo()
                : {};

            logger.error('fetchPictureByPlaceId', ex, {
                snowplowIds: {
                    networkUserId: SnowplowService.getNetworkUserId(),
                    ...domainUserInfo,
                },
            });
        }
    };
}

export function fetchPopularCityInfo(wcityid) {
    return async (dispatch) => {
        try {
            const response = await CityService.getCityByWCityId(wcityid);
            const result = _formatPopularCityInfo(response.result[0].google, wcityid);
            const placeId = result.location.placeId;

            dispatch(setCityLocation(wcityid, result));
            return dispatch(fetchPictureByPlaceId(placeId, wcityid));
        } catch (error) {
            // TODO: Error handling
            const domainUserInfo = appSettings.env.isClient
                ? SnowplowService.getDomainUserInfo()
                : {};

            logger.error('fetchPopularCityInfo', error, {
                snowplowIds: {
                    networkUserId: SnowplowService.getNetworkUserId(),
                    ...domainUserInfo,
                },
            });
        }
    };
}

export function removeAllCities() {
    return { type: REMOVE_ALL_CITIES };
}

export function setAllCitiesInactive() {
    return { type: SET_ALL_CITIES_INACTIVE };
}

export function setCityActive(wcityid) {
    return { type: SET_CITY_ACTIVE, wcityid };
}

function _setCityLocation(state, wcityid, result) {
    // TODO: See if cities and pricings can be combined
    if (wcityid && result) {
        return [
            ...state.filter((city) => city.location.wcityid !== wcityid),
            result,
        ];
    }

    return state;
}

function _setCityPicture(state, wcityid, photourl) {
    if (wcityid && photourl) {
        return state.map((city) => {
            if (city.location.wcityid !== wcityid) {
                return city;
            }
            return { ...city, photourl };
        });
    }

    return state;
}

function _setAllCitiesInactive(state) {
    return state.map((city) => ({ ...city, active: false }));
}

function _setCityActive(state, wcityid) {
    if (wcityid) {
        return state.map((city) => {
            if (city.location.wcityid !== wcityid) {
                return city;
            }

            return { ...city, active: true };
        });
    }

    return state;
}

function _removeAllCities() {
    return INITIAL_STATE;
}

export default function reducer(state = INITIAL_STATE, action) {
    switch (action.type) {
        case SET_CITY_LOCATION:
            return _setCityLocation(state, action.wcityid, action.result);
        case SET_CITY_PICTURE:
            return _setCityPicture(state, action.wcityid, action.url);
        case SET_ALL_CITIES_INACTIVE:
            return _setAllCitiesInactive(state);
        case SET_CITY_ACTIVE:
            return _setCityActive(state, action.wcityid);
        case REMOVE_ALL_CITIES:
            return _removeAllCities();
        default:
            return state;
    }
}

export function setCityLocation(wcityid, result) {
    return { type: SET_CITY_LOCATION, wcityid, result };
}

function _formatPopularCityInfo(google, wcityid) {
    const googleLocation = formatGoogleLocation(google.details);

    return {
        active: true,
        location: {
            lat: googleLocation.lat,
            lng: googleLocation.lng,
            city: googleLocation.city,
            state: googleLocation.state,
            country: googleLocation.countryShort,
            formattedAddress: googleLocation.formattedAddress,
            placeId: googleLocation.placeId,
            wcityid,
        },
    };
}

export function setCityPicture(wcityid, url) {
    return { type: SET_CITY_PICTURE, wcityid, url };
}

const _selectActiveCities = createSelector(
    _selectCities,
    (cities) => cities.filter((city) => city.active),
);

export const validCitiesSelector = createSelector(
    _selectActiveCities,
    _selectPricings,
    _selectPriceFilter,
    _selectCityToCityIds,
    _selectFilteredAndSortCities,
);

function _selectCities(appState) {
    return appState.explorer.popularCities;
}

function _selectPricings(appState) {
    return appState.explorer.pricings;
}

function _selectPriceFilter(appState) {
    return appState.explorer.filters.price;
}

function _selectCityToCityIds(appState) {
    const { originCities } = appState.explorer;
    const activeCity = originCities[originCities.activeWcityid];
    if (!activeCity) {
        return null;
    }

    return activeCity.cityToCityIds;
}

function _filterCitiesByPrice(city, pricing, price) {
    if (!pricing || !city.active) {
        return false;
    }

    if (!price) {
        return true;
    }

    const { cheapestMode } = pricing;
    const { min } = pricing[cheapestMode].USD;

    return min < price;
}

function _selectFilteredAndSortCities(popularCities, pricings, price, cityToCityIds) {
    if (!cityToCityIds) {
        return;
    }

    const cityToCities = cityToCityIds.map((cityToCityId) => cityToCityId.split('_')[1]);
    return popularCities
        .filter((city) => {
            const { wcityid } = city.location;
            const pricing = pricings.cities[wcityid];
            return _filterCitiesByPrice(city, pricing, price);
        })
        .sort((prevCity, currCity) => {
            return _sortCitiesByPopularity(prevCity, currCity, cityToCities);
        });
}

function _sortCitiesByPopularity(prevCity, currCity, cityToCities) {
    const prevPopularity = cityToCities.indexOf(prevCity.location.wcityid);
    const currPopularity = cityToCities.indexOf(currCity.location.wcityid);

    if (prevPopularity < currPopularity) {
        return -1;
    }

    if (prevPopularity > currPopularity) {
        return 1;
    }

    return 0;
}
